import { type AnyAction, isFulfilled, isRejected, type Middleware } from '@reduxjs/toolkit'
import posthog from 'posthog-js'

export const ANALYTICS_EVENTS = {
  postCreated: 'post_created',
  postCreateFailed: 'post_create_failed',
  postPublished: 'post_published',
  postPublishFailed: 'post_publish_failed',
  campaignCreated: 'campaign_created',
  campaignCreateFailed: 'campaign_create_failed',
  socialConnectStarted: 'social_account_connect_started',
  socialConnectFailed: 'social_account_connect_failed',
  onboardingSkipped: 'onboarding_skipped',
} as const

const PLATFORM_BY_CREATE_ENDPOINT: Record<string, string> = {
  createInstagramPost: 'instagram',
  createFacebookPost: 'facebook',
  createTiktokPost: 'tiktok',
}

const endpointNameOf = (action: AnyAction): string | undefined => {
  const endpointName = action.meta?.arg?.endpointName

  return typeof endpointName === 'string' ? endpointName : undefined
}

const originalArgsOf = (action: AnyAction): Record<string, unknown> => {
  const originalArgs = action.meta?.arg?.originalArgs

  return typeof originalArgs === 'object' && originalArgs !== null
    ? (originalArgs as Record<string, unknown>)
    : {}
}

const failureReasonOf = (action: AnyAction): Record<string, unknown> => {
  const status = action.payload?.status

  return status === undefined ? {} : { status }
}

const isDraft = (action: AnyAction): boolean => originalArgsOf(action).draft === true

const platformArgOf = (action: AnyAction): string | undefined => {
  const platform = originalArgsOf(action).platform

  return typeof platform === 'string' ? platform : undefined
}

const captureFulfilled = (endpointName: string, action: AnyAction): void => {
  const platform = PLATFORM_BY_CREATE_ENDPOINT[endpointName]

  if (platform !== undefined) {
    posthog.capture(ANALYTICS_EVENTS.postCreated, { platform, draft: isDraft(action) })
    return
  }

  switch (endpointName) {
    case 'sendPost':
      posthog.capture(ANALYTICS_EVENTS.postPublished)
      break
    case 'createCampaign':
      posthog.capture(ANALYTICS_EVENTS.campaignCreated)
      break
    case 'connectSocialAccount':
      posthog.capture(ANALYTICS_EVENTS.socialConnectStarted, { platform: platformArgOf(action) })
      break
    case 'logout':
      posthog.reset()
      break
    default:
      break
  }
}

const captureRejected = (endpointName: string, action: AnyAction): void => {
  const platform = PLATFORM_BY_CREATE_ENDPOINT[endpointName]

  if (platform !== undefined) {
    posthog.capture(ANALYTICS_EVENTS.postCreateFailed, {
      platform,
      draft: isDraft(action),
      ...failureReasonOf(action),
    })
    return
  }

  switch (endpointName) {
    case 'sendPost':
      posthog.capture(ANALYTICS_EVENTS.postPublishFailed, failureReasonOf(action))
      break
    case 'createCampaign':
      posthog.capture(ANALYTICS_EVENTS.campaignCreateFailed, failureReasonOf(action))
      break
    case 'connectSocialAccount':
      posthog.capture(ANALYTICS_EVENTS.socialConnectFailed, {
        platform: platformArgOf(action),
        ...failureReasonOf(action),
      })
      break
    default:
      break
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const analyticsMiddleware: Middleware<{}, any> = () => (next) => (action) => {
  const result = next(action)
  const endpointName = endpointNameOf(action)

  if (endpointName === undefined) {
    return result
  }

  if (isFulfilled(action)) {
    captureFulfilled(endpointName, action)
  } else if (isRejected(action)) {
    captureRejected(endpointName, action)
  }

  return result
}
